import { Trans, useTranslation } from 'react-i18next';
import React, { Fragment, useMemo } from 'react';
import STRING_KEYS from '../../../../../../../../language/keys';
import ExplainabilityWidgetItem from '../explainability-widget-item.component';
import { CompanyFundingStage } from '../../../../../../../../enums/company-funding-stage.enum';
import {
  Container,
  Description, Icon, Title,
} from './explainability-widget-company.styled';
import { ExplainabilityWidgetCompanySizeProps } from './explainability-widget-company';

type RelevantCompanySizesSummery = {
  sizes: string[];
  companies: string[];
}

type RelevantFundingStages = {
  stage: CompanyFundingStage;
  companies: string[];
}

const ExplainabilityWidgetCompany = ({ employmentHistory }: ExplainabilityWidgetCompanySizeProps): JSX.Element | null => {
  const { t: translate } = useTranslation();

  const relevantCompanySizesSummery: RelevantCompanySizesSummery = useMemo<RelevantCompanySizesSummery | undefined>(() => {
    const actualCompanySizes = employmentHistory?.reduce((acc: { sizesSet: Set<string>; companies: string[] }, history) => {
      if (history.companySizeTier?.askedByUser) {
        acc.sizesSet.add(translate(STRING_KEYS.COMPANY_SIZE[history.companySizeTier.value]));
        acc.companies.push(history.companyName);
      }

      return acc;
    }, {
      sizesSet: new Set<string>(), // using a Set to collect unique sizes
      companies: [],
    });

    if (!actualCompanySizes || actualCompanySizes.sizesSet.size === 0) {
      return null;
    }

    return {
      sizes: Array.from(actualCompanySizes.sizesSet),
      companies: actualCompanySizes.companies,
    };
  }, [employmentHistory, translate]);

  const preferredCompanySummery = useMemo<string[]>(() => {
    const preferredCompanies = employmentHistory?.reduce((acc, history) => {
      if (history.isPreferredCompany) {
        acc.add(history.companyName);
      }

      return acc;
    }, new Set<string>());

    if (preferredCompanies.size === 0) {
      return null;
    }

    return Array.from(preferredCompanies);
  }, [employmentHistory]);

  const relevantFundingStages = useMemo<RelevantFundingStages[]>(() => {
    const stageToCompaniesMap = employmentHistory.reduce((acc: Partial<Record<CompanyFundingStage, Set<string>>>, record) => {
      const relevantCompanyFundingStage = record.explainability?.relevantFundingStages?.filter((fundingStage) => {
        return fundingStage !== CompanyFundingStage.Other;
      });
      if (!relevantCompanyFundingStage?.length) return acc;

      relevantCompanyFundingStage.forEach((stage) => {
        if (!acc[stage]) {
          acc[stage] = new Set();
        }
        acc[stage].add(record.companyName);
      });

      return acc;
    }, {});

    return Object.entries(stageToCompaniesMap).map<RelevantFundingStages>(([stage, companies]) => ({
      stage: stage as CompanyFundingStage,
      companies: Array.from(companies),
    }));
  }, [employmentHistory]);

  const renderRelevantFundingStages = () => {
    if (!relevantFundingStages.length) {
      return undefined;
    }

    return (
      <>
        {
          relevantFundingStages.map((relevantFundingStage) => {
            return (
              <Fragment key={ relevantFundingStage.stage }>
                <div>
                  <Trans
                    i18nKey={ STRING_KEYS.COMPANY_FUNDING_STAGE_DESCRIPTION }
                    values={ {
                      stage: translate(STRING_KEYS.COMPANY_FUNDING_STAGES[relevantFundingStage.stage]),
                      companies: relevantFundingStage.companies.join(', '),
                    } }
                    components={ {
                      Title: <Title />,
                      Description: <Description />,
                    } }
                  />
                </div>
              </Fragment>
            );
          })
        }
      </>
    );
  };

  if (
    (!preferredCompanySummery || preferredCompanySummery.length === 0) &&
    (!relevantFundingStages || relevantFundingStages.length === 0) &&
    (!relevantCompanySizesSummery || relevantCompanySizesSummery.sizes.length === 0)
  ) {
    return null;
  }

  return (
    <ExplainabilityWidgetItem
      key="ExplainabilityWidgetCompanySize"
      label={ translate(STRING_KEYS.MATCH_CARD.EXPLAINABILITY_WIDGET.ITEMS_LABELS.COMPANY) }
      Icon={ Icon }
    >
      <Container>
        { preferredCompanySummery && (
          <div>
            <Trans
              i18nKey={ STRING_KEYS.COMPANY_PREFERRED_DESCRIPTION }
              values={ {
                companies: preferredCompanySummery.join(', '),
              } }
              components={ {
                Title: <Title />,
                Description: <Description />,
              } }
            />
          </div>
        ) }
        {
          renderRelevantFundingStages()
        }
        { relevantCompanySizesSummery && (
          <div>
            <Trans
              i18nKey={ STRING_KEYS.COMPANY_SIZE_DESCRIPTION }
              values={ {
                sizes: relevantCompanySizesSummery.sizes.join(', '),
                companies: relevantCompanySizesSummery.companies.join(', '),
              } }
              components={ {
                Title: <Title />,
                Description: <Description />,
              } }
            />
          </div>
        ) }
      </Container>
    </ExplainabilityWidgetItem>
  );
};

export default ExplainabilityWidgetCompany;
